/** Identifies editor-only variable tokens that must never enter saved questionnaire HTML. */
export const VARIABLE_TOKEN_SELECTOR = 'span[data-qa-variable]';

/** Creates an atomic token whose underlying text remains the original Handlebars expression. */
export function createVariableToken(expression: string, label: string): HTMLSpanElement {
  const token = document.createElement('span');
  token.className = 'qa-variable-token fr-deletable';
  token.setAttribute('data-qa-variable', 'true');
  token.setAttribute('data-qa-variable-label', label);
  token.setAttribute('contenteditable', 'false');
  token.setAttribute('tabindex', '0');
  token.setAttribute('role', 'button');
  token.setAttribute('aria-label', `${label}: ${expression}`);
  token.title = expression;
  token.textContent = expression;
  return token;
}

/** Removes presentation wrappers while preserving the original expressions and surrounding formatting. */
export function serializeVariableTokens(html: string): string {
  if (!html || html.indexOf('data-qa-variable') === -1) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll(VARIABLE_TOKEN_SELECTOR).forEach(token => {
    const markers = Array.from(token.querySelectorAll('.fr-marker, .fr-tmp'));
    markers.forEach(marker => marker.remove());
    token.replaceWith(document.createTextNode(token.textContent || ''), ...markers);
  });
  return template.innerHTML;
}

/** Decorates simple expressions only, leaving helpers, blocks, attributes and existing formatting untouched. */
export function decorateVariableTokens(root: Element, labels: {[path: string]: string}): boolean {
  let changed = false;
  root.querySelectorAll(VARIABLE_TOKEN_SELECTOR).forEach(token => {
    const path = (token.textContent || '').replace(/^\{\{\s*|\s*\}\}$/g, '').replace(/^patient\./, 'client.');
    const label = labels[path] || path;
    token.setAttribute('data-qa-variable-label', label);
    token.setAttribute('aria-label', `${label}: ${token.textContent}`);
  });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node => node.parentElement?.closest(`${VARIABLE_TOKEN_SELECTOR}, [data-qa-condition], script, style, .fr-marker`)
      ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
  });
  const nodes: Text[] = [];
  let node: Node;
  while ((node = walker.nextNode())) {
    nodes.push(node as Text);
  }
  nodes.forEach(text => {
    const pattern = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.-]*)\s*\}\}/g;
    const content = text.data;
    const matches: {offset: number; expression: string; path: string}[] = [];
    let match: RegExpExecArray;
    while ((match = pattern.exec(content))) {
      if (match[1] === 'else' || content[match.index - 1] === '{' || content[match.index + match[0].length] === '}') {
        continue;
      }
      matches.push({offset: match.index, expression: match[0], path: match[1]});
    }
    matches.reverse().forEach(item => {
      changed = true;
      const tokenText = text.splitText(item.offset);
      tokenText.splitText(item.expression.length);
      const canonicalPath = item.path.replace(/^patient\./, 'client.');
      tokenText.replaceWith(createVariableToken(item.expression, labels[canonicalPath] || item.path));
    });
  });
  return changed;
}

/** Adds only missing word separators, preserving existing spaces and attached punctuation. */
export function variableInsertionSpacing(before: string, after: string): {prefix: string; suffix: string} {
  const previous = before.slice(-1);
  const next = after.slice(0, 1);
  return {
    prefix: previous && !/[\s([{"'«„“]/.test(previous) ? ' ' : '',
    suffix: !next || !/[\s,.;:!?%)\]}"'»”]/.test(next) ? ' ' : ''
  };
}
